import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Typography, useMediaQuery } from '@mui/material';

import useOffSetTop from 'src/hooks/useOffSetTop';
import useResponsive from 'src/hooks/useResponsive';
import useCollapseDrawer from 'src/hooks/useCollapseDrawer';
import cssStyles from '../../../utils/cssStyles';
import useMerchant from 'src/hooks/useMerchant';
import { HEADER, NAVBAR } from '../../../config';
import Logo from 'src/components/Logo';
import AccountPopover from './AccountPopover';
import TopAlertMessage from 'src/components/subscription/TopAlertMessage';
import CollapseButton from '../navbar/CollapseButton';
import TopNavbar from '../navbar/TopNavbar';

//
//

type RootStyleProps = {
  isCollapse: boolean;
  isOffset: boolean;
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' && prop !== 'isOffset',
})<RootStyleProps>(({ isCollapse, isOffset, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  border: 'none',
  boxShadow: 'none',
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
  },
  '& .MuiChip-root': {
    color: theme.palette.text.primary,
    '&:hover': {
      '& .MuiTypography-root': {
        fontFamily: 'ObjektivMk1 !important',
        fontWeight: 500,
      },
      '& .MuiSvgIcon-root': {
        transform: 'scale(1.1)'
      },
    },
    '&:active': {
      boxShadow: 'none',
    }
  }
}));

// ----------------------------------------------------------------------

type Props = {
  onOpenSidebar: VoidFunction;
  isCollapse?: boolean;
};

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
}: Props) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT);

  const isDesktop = useResponsive('up', 'lg');
  const mdUp = useResponsive('up', 'md');
  const isMobile = useMediaQuery('(max-width:475px)');

  const { merchant } = useMerchant();
  const { collapseClick } = useCollapseDrawer();

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset}>

      <TopAlertMessage />

      <Toolbar
        sx={{
          minHeight: '80px !important',
          // adds transparency of header after scrolling
          // backgroundColor: alpha(theme.palette?.brandedColors?.base?.paper, 0.5),
          px: '1rem !important',
          py: 2,
        }}
      >

        {!isDesktop && (
          <CollapseButton onToggleCollapse={onOpenSidebar} collapseClick={collapseClick} sx={{ mr: 1 }} />
        )}

        {!mdUp && <Logo />}

        {isMobile && <Box sx={{ flexGrow: 1 }} />}

        <Stack direction={'row'} gap={2} alignItems={'center'} sx={{ '& .MuiChip-label': { pl: 1 } }}>
          {isCollapse && <Logo />}

          {mdUp && <TopNavbar />}
        </Stack>

        {!isMobile && <Box sx={{ flexGrow: 1 }} />}

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {mdUp &&
            <Typography
              variant='body2'
              sx={{
                color: 'text.primary',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: { xs: 350, md: '100%' }
              }}
            >
              {merchant?.name}
            </Typography>
          }
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
